import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import WorkListPage from '../../../components/work/WorkListPage';
import { taskboardsColumnsApi, tasksApi } from '../../../utils/workApi';
import { buildTaskboardColumnPayload } from '../../../utils/workPayloadBuilders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { Button, Modal, Form, Input, message, Popconfirm, Space, Spin } from 'antd';
import WorkColorPickerField from '../../../components/work/WorkColorPickerField';
import {
  PlusOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  EditOutlined,
  AppstoreOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const SortableColumnRow = ({ column, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    boxShadow: isDragging ? '0 6px 18px rgba(0,0,0,0.12)' : undefined,
    zIndex: isDragging ? 10 : undefined,
    fontFamily: 'CustomFont',
  };
  const color = column.label_color || column.color;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 mb-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 touch-none bg-transparent border-0"
        aria-label="جابجایی اولویت"
        title="درگ برای تغییر اولویت"
        {...attributes}
        {...listeners}
      >
        <HolderOutlined className="text-lg" />
      </button>

      <span
        className="inline-block w-6 h-6 rounded border shrink-0"
        style={{ background: color || '#e5e7eb' }}
        title={color || 'بدون رنگ'}
      />

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
          {column.column_name}
        </div>
        <div className="text-xs text-gray-500">اولویت: {column.priority ?? '—'}</div>
      </div>

      <Space>
        <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(column)} />
        <Popconfirm
          title="حذف ستون؟"
          onConfirm={() => onDelete(column)}
          okText="حذف"
          cancelText="انصراف"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    </div>
  );
};

const TaskBoardSettings = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortSaving, setSortSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortedColumns = useMemo(
    () =>
      [...columns].sort((a, b) => {
        const pa = Number(a.priority ?? a.id ?? 0);
        const pb = Number(b.priority ?? b.id ?? 0);
        return pa - pb;
      }),
    [columns],
  );

  const ids = useMemo(() => sortedColumns.map((c) => c.id), [sortedColumns]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksApi.getBoard({});
      setColumns(res?.boardColumns || res?.columns || res?.taskboard_columns || []);
    } catch {
      message.error('خطا در بارگذاری ستون‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = async (row) => {
    setEditId(row.id);
    try {
      const res = await taskboardsColumnsApi.getEditData(row.id);
      const col = res?.column || res?.taskboard_column || row;
      form.setFieldsValue(col);
    } catch {
      form.setFieldsValue(row);
    }
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const body = buildTaskboardColumnPayload(values);
      if (editId) await taskboardsColumnsApi.update(editId, body);
      else await taskboardsColumnsApi.create(body);
      message.success('ذخیره شد');
      setOpen(false);
      load();
    } catch {
      message.error('خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await taskboardsColumnsApi.delete(row.id);
      message.success('حذف شد');
      load();
    } catch {
      message.error('خطا در حذف');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(sortedColumns, oldIndex, newIndex).map((col, index) => ({
      ...col,
      priority: index + 1,
    }));
    setColumns(reordered);

    setSortSaving(true);
    try {
      await Promise.all(
        reordered.map((col, index) =>
          taskboardsColumnsApi.update(col.id, {
            column_name: col.column_name,
            label_color: col.label_color || col.color,
            priority: index + 1,
          }),
        ),
      );
      message.success('ترتیب ستون‌ها ذخیره شد');
    } catch {
      message.error('خطا در ذخیره ترتیب');
      load();
    } finally {
      setSortSaving(false);
    }
  };

  return (
    <WorkListPage
      title="ستون‌های برد وظایف"
      subtitle="مدیریت وضعیت‌های کانبان — با درگ ترتیب/اولویت را تغییر دهید"
      icon={AppstoreOutlined}
      headerExtra={(
        <Space wrap>
          <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/work/tasks')} style={{ fontFamily: 'CustomFont' }}>
            بازگشت
          </Button>
          <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openCreate} style={{ fontFamily: 'CustomFont' }}>
            ستون جدید
          </Button>
        </Space>
      )}
      footer={(
        <Modal
          open={open}
          title={<span style={{ fontFamily: 'CustomFont' }}>{editId ? 'ویرایش ستون' : 'ستون جدید'}</span>}
          onCancel={() => setOpen(false)}
          onOk={submit}
          confirmLoading={submitting}
          destroyOnClose
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Form form={form} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item name="column_name" label="نام ستون" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <WorkColorPickerField name="label_color" label="رنگ" />
            <Form.Item name="priority" label="اولویت نمایش">
              <Input type="number" />
            </Form.Item>
          </Form>
        </Modal>
      )}
    >
      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <div style={{ fontFamily: 'CustomFont' }}>
          <p className="text-gray-600 dark:text-gray-400 mb-3 text-sm">
            ردیف‌ها را بکشید تا اولویت نمایش ستون‌ها تغییر کند و ذخیره شود.
          </p>
          {sortSaving ? (
            <div className="mb-3 text-sm text-gray-500">
              <Spin size="small" /> <span className="mr-2">در حال ذخیره ترتیب...</span>
            </div>
          ) : null}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {sortedColumns.map((col) => (
                <SortableColumnRow
                  key={col.id}
                  column={col}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </SortableContext>
          </DndContext>
          {sortedColumns.length === 0 ? (
            <div className="text-center text-gray-500 py-12">ستونی ثبت نشده است</div>
          ) : null}
        </div>
      )}
    </WorkListPage>
  );
};

export default TaskBoardSettings;
